import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';

const DEFAULTS = {
  name: 'FlockTrade',
  title: 'FlockTrade · Market Intelligence Terminal for Serious Traders',
  description: 'Market intelligence terminal for serious self-directed traders. 90+ panels, options flow, multi-timeframe sentiment, geopolitical intelligence and an AI research assistant. From $29/mo.',
  image: 'https://flocktrade.com/og-flocktrade.jpg',
  imageWidth: 1200,
  imageHeight: 630,
  url: 'https://flocktrade.com',
};

const SEO = ({
  title,
  description,
  path = '',
  image,
  imageWidth,
  imageHeight,
  imageAlt,
  type = 'website',
  noindex = false,
  jsonLd,
}) => {
  const { tenantConfig } = useTheme();
  const { i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'en';
  const ogLocale = { en: 'en_US', no: 'nb_NO', nb: 'nb_NO', da: 'da_DK', sv: 'sv_SE' }[lang] || 'en_US';
  const siteName = tenantConfig?.name || DEFAULTS.name;
  const siteUrl = tenantConfig?.slug && tenantConfig.slug !== 'flocktrade'
    ? `https://${tenantConfig.slug}.flocktrade.com`
    : DEFAULTS.url;
  const siteImage = image || tenantConfig?.logo || DEFAULTS.image;
  const siteDescription = description || DEFAULTS.description;
  const fullTitle = title ? `${title} | ${siteName}` : DEFAULTS.title;
  const canonicalUrl = `${siteUrl}${path}`;
  // Only emit image dimensions when they are actually known: explicit props,
  // or the default share image (a known 1200x630). Tenant logos have unknown
  // dimensions, so we omit the tags rather than assert a wrong size.
  const imgW = imageWidth || (siteImage === DEFAULTS.image ? DEFAULTS.imageWidth : null);
  const imgH = imageHeight || (siteImage === DEFAULTS.image ? DEFAULTS.imageHeight : null);
  const imgAlt = imageAlt || fullTitle;
  const robotsContent = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={siteDescription} />
      <link rel="canonical" href={canonicalUrl} />
      <meta name="robots" content={robotsContent} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={siteDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={siteImage} />
      {imgW && <meta property="og:image:width" content={String(imgW)} />}
      {imgH && <meta property="og:image:height" content={String(imgH)} />}
      <meta property="og:image:alt" content={imgAlt} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={siteDescription} />
      <meta name="twitter:image" content={siteImage} />
      <meta name="twitter:image:alt" content={imgAlt} />

      {/* JSON-LD */}
      {jsonLd && (Array.isArray(jsonLd)
        ? jsonLd.map((ld, i) => (
            <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
          ))
        : <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
